/**
 * Avisos del teléfono ADENTRO DE LA APP (iPhone), por APNs.
 *
 * **Lo que se registra en el backend sigue siendo un token de FCM, no el de
 * APNs**, y eso no es un detalle de implementación: el backend manda por FCM
 * HTTP v1 con un bloque `apns`, y FCM no sabe qué hacer con un token crudo de
 * Apple. Por eso acá va el SDK nativo de Firebase
 * (`@capacitor-firebase/messaging`) y no `@capacitor/push-notifications`, que
 * devuelve el token de APNs pelado: con ese, cada envío sería rechazado y —por
 * cómo el backend interpreta el rechazo— el dispositivo se borraría solo del
 * padrón. Mismo caño que la PWA y que Android; cambia quién consigue el token.
 *
 * El `platform` que se manda es `"ios"`, que el backend ya acepta.
 *
 * AL PLUGIN SE LE HABLA POR EL PUENTE, SIN SU PAQUETE JS. El paquete era un
 * `import()` dinámico, y el 3-11 ese mismo `import()` —el del plugin hermano,
 * el de autenticación— se colgó para siempre en un iPhone con iOS 26.1: ni
 * resolvió ni falló (la historia entera está en `loginNativo.ts`). Acá el
 * síntoma habría sido peor, porque nadie espera un resultado: los avisos
 * simplemente no se registrarían nunca y nadie se enteraría. El puente que el
 * contenedor inyecta antes del bundle es la mitad que sí contestó, y el
 * paquete queda de plan B —dinámico, para que el navegador no arrastre nada.
 */

import { apiFetch } from "../api/client";
import { escucharNativo, llamarNativo } from "../lib/nativo";
import type { PushStatus } from "./webPush";

const LAST_TOKEN_KEY = "forja.push_token";
/** El plugin nativo, por su nombre en el puente. */
const PLUGIN = "FirebaseMessaging";

let enganchado = false;

async function plugin() {
  const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
  return FirebaseMessaging;
}

/** Lo que contesta el plugin, por los dos caminos. */
type Permiso = { receive: "granted" | "denied" | "prompt" | "prompt-with-rationale" };
type Token = { token: string };

/** Pedirle algo al plugin: por el puente si está, por el paquete si no. */
async function pedir<T>(
  metodo: "checkPermissions" | "requestPermissions" | "getToken" | "deleteToken",
): Promise<T> {
  const directo = llamarNativo<T>(PLUGIN, metodo);
  if (directo) return directo;
  const FirebaseMessaging = await plugin();
  return (await FirebaseMessaging[metodo]()) as T;
}

function guardado(): string | null {
  try {
    return localStorage.getItem(LAST_TOKEN_KEY);
  } catch {
    return null;
  }
}

function guardar(token: string | null): void {
  try {
    if (token) localStorage.setItem(LAST_TOKEN_KEY, token);
    else localStorage.removeItem(LAST_TOKEN_KEY);
  } catch {
    /* modo privado: que no rompa el ingreso */
  }
}

async function registrar(token: string): Promise<void> {
  await apiFetch("/api/v1/me/device-tokens", {
    method: "POST",
    body: { token, platform: "ios", granted: true },
  });
  guardar(token);
}

/** En el iPhone el permiso lo decide iOS; el estado se le pregunta a él. */
export async function estadoNativo(): Promise<PushStatus> {
  try {
    const { receive } = await pedir<Permiso>("checkPermissions");
    if (receive === "denied") return "blocked";
    if (receive === "granted" && guardado()) return "enabled";
    return "off";
  } catch {
    return "unsupported";
  }
}

/**
 * Pide el permiso y registra el token. Hay que llamarlo DESDE UN GESTO: iOS
 * muestra el cartel de permiso una sola vez en la vida de la instalación, así
 * que gastarlo en un arranque automático es perderlo para siempre.
 */
export async function activarNativo(): Promise<PushStatus> {
  try {
    const { receive } = await pedir<Permiso>("requestPermissions");
    if (receive !== "granted") return receive === "denied" ? "blocked" : "off";
    const { token } = await pedir<Token>("getToken");
    if (!token) return "off";
    await registrar(token);
    engancharNativo();
    return "enabled";
  } catch {
    return "unsupported";
  }
}

export async function apagarNativo(): Promise<void> {
  const token = guardado();
  guardar(null);
  if (token) {
    await apiFetch("/api/v1/me/device-tokens/remove", {
      method: "POST",
      body: { token },
    }).catch(() => undefined);
  }
  try {
    await pedir<void>("deleteToken");
  } catch {
    /* si el plugin no está, el backend ya se enteró: alcanza */
  }
}

/**
 * El token de FCM ROTA sin avisar (reinstalación, restore de backup). Si no se
 * escucha ese cambio, el teléfono deja de recibir avisos y nadie se entera:
 * el backend sigue mandando a un token muerto. Por eso se escucha, además de
 * re-pedirlo al arrancar.
 */
export function engancharNativo(): void {
  if (enganchado) return;
  enganchado = true;
  const alRotar = (event: { token: string }) => {
    // Puede llegar sin sesión (el token rota mientras la app está en el
    // login): el 401 no es un error que haya que reportar, y el token se
    // vuelve a pedir al retomar. Sin el catch quedaba como rechazo sin
    // atender, que Sentry cuenta como error.
    if (event.token && event.token !== guardado()) {
      void registrar(event.token).catch(() => undefined);
    }
  };
  if (escucharNativo<Token>(PLUGIN, "tokenReceived", alRotar)) return;
  void plugin().then((FirebaseMessaging) => {
    void FirebaseMessaging.addListener("tokenReceived", alRotar);
  });
}

/** Al abrir la app con los avisos ya activados: re-registra y engancha. */
export async function retomarNativo(): Promise<void> {
  if (!guardado()) return;
  try {
    const { receive } = await pedir<Permiso>("checkPermissions");
    if (receive !== "granted") return;
    const { token } = await pedir<Token>("getToken");
    if (token && token !== guardado()) await registrar(token);
    engancharNativo();
  } catch {
    /* sin plugin no hay nada que retomar */
  }
}
